/**
 * Construtores de dados estruturados complementares (Schema.org).
 * Não altera o schema individual do imóvel — apenas schemas auxiliares.
 */

import { getPropertyTypeLabel } from '../../constants/propertyTypes';
import { buildPropertyUrl } from './urlHelpers';
import {
  truncateSeoTitle,
  truncateSeoDescription,
  normalizeProductionUrl,
  stripUndefinedDeep,
} from './metaHelpers';

/** Remove vazios, espaços e URLs duplicadas de sameAs. */
export function normalizeSameAsUrls(urls = []) {
  const seen = new Set();
  const out = [];
  for (const raw of urls) {
    if (raw == null) continue;
    let u = String(raw).trim();
    if (!u) continue;
    if (!/^https?:\/\//i.test(u)) {
      if (u.startsWith('//')) u = `https:${u}`;
      else if (/^(www\.|instagram\.|facebook\.|linkedin\.|twitter\.|x\.)/i.test(u)) {
        u = `https://${u}`;
      } else {
        continue;
      }
    }
    u = u.replace(/^http:\/\//i, 'https://');
    try {
      // eslint-disable-next-line no-new
      new URL(u);
    } catch {
      continue;
    }
    const key = u.replace(/\/+$/, '').toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(u);
  }
  return out;
}

function formatSchemaTelephone(raw) {
  if (!raw || typeof raw !== 'string') return undefined;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;
  if (digits.startsWith('55') && digits.length >= 12) return `+${digits}`;
  if (digits.length >= 10) return `+55${digits}`;
  return raw.trim() || undefined;
}

function normalizeClockTime(value) {
  const m = String(value || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return `${String(m[1]).padStart(2, '0')}:${m[2]}`;
}

function resolveSchemaMediaUrl(settings, baseUrl) {
  const raw =
    settings?.seoOgImage ||
    settings?.logo ||
    settings?.logoDark ||
    settings?.logoLight ||
    '';
  if (!raw || typeof raw !== 'string') return undefined;
  return normalizeProductionUrl(raw, baseUrl) || undefined;
}

function buildBusinessPostalAddress(settings) {
  const structured = settings?.contactAddressStructured || {};
  const street = settings?.contactLogradouro || structured.logradouro || '';
  const number = settings?.contactNumero || structured.numero || '';
  const district = settings?.contactBairro || structured.bairro || '';
  const city =
    settings?.contactCidade ||
    structured.localidade ||
    settings?.seoServiceArea?.split(',')[0]?.trim() ||
    '';
  const region =
    settings?.contactEstado ||
    structured.uf ||
    settings?.seoServiceArea?.split(',')[1]?.trim() ||
    '';
  const postalCode = settings?.contactCep || structured.cep || '';

  const streetAddress =
    [street, number, district].filter(Boolean).join(', ') ||
    settings?.contactAddress ||
    settings?.fullAddress ||
    '';

  if (!streetAddress && !city && !region && !postalCode) {
    return undefined;
  }

  return stripUndefinedDeep({
    '@type': 'PostalAddress',
    streetAddress: streetAddress || undefined,
    addressLocality: city || undefined,
    addressRegion: region || undefined,
    postalCode: postalCode || undefined,
    addressCountry: 'BR',
  });
}

function resolvePriceRange(settings) {
  const custom = String(settings?.seoPriceRange || '').trim();
  if (custom) return custom;
  return '$$';
}

/** openingHoursSpecification (Schema.org), a partir de contactHours estruturado. */
export function buildOpeningHoursSpecification(settings) {
  const hours = settings?.contactHours;
  if (!hours || typeof hours !== 'object') return undefined;

  const specs = [];
  const pushRange = (dayOfWeek, block) => {
    if (!block || block.closed) return;
    const opens = normalizeClockTime(block.from);
    const closes = normalizeClockTime(block.to);
    if (!opens || !closes) return;
    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek,
      opens,
      closes,
    });
  };

  pushRange(
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    hours.weekdays
  );
  pushRange('Saturday', hours.saturdays);
  pushRange('Sunday', hours.sundays);

  return specs.length ? specs : undefined;
}

export function buildAreaServed(settings) {
  const raw = String(settings?.seoServiceArea || '').trim();
  const structured = settings?.contactAddressStructured || {};
  const city =
    settings?.contactCidade ||
    structured.localidade ||
    (raw ? raw.split(',')[0]?.trim() : '') ||
    '';
  const region =
    settings?.contactEstado ||
    structured.uf ||
    (raw.includes(',') ? raw.split(',')[1]?.trim() : '') ||
    '';

  if (!raw && !city) return undefined;

  const name = raw || (region ? `${city}, ${region}` : city);
  return stripUndefinedDeep({
    '@type': 'AdministrativeArea',
    name,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city || undefined,
      addressRegion: region || undefined,
      addressCountry: 'BR',
    },
  });
}

export function resolveTwitterSiteHandle(settings) {
  const raw = String(settings?.socialTwitter || '').trim();
  if (!raw) return null;
  if (raw.startsWith('@') && /^@[\w]+$/.test(raw)) return raw;
  const m = raw.match(/(?:twitter\.com|x\.com)\/(@?[\w]+)/i);
  if (m) {
    const handle = m[1].replace(/^@/, '');
    if (handle && !/^(home|share|intent)$/i.test(handle)) return `@${handle}`;
  }
  if (/^[\w]+$/.test(raw)) return `@${raw}`;
  return null;
}

export function buildWebSiteSchema(settings, baseUrl) {
  const base = (baseUrl || '').replace(/\/$/, '');
  return stripUndefinedDeep({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${base}/#website`,
    name: settings?.companyName || 'Imobiliária',
    url: `${base}/`,
    inLanguage: 'pt-BR',
    description: settings?.seoMetaDescription || undefined,
    publisher: { '@id': `${base}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${base}/propriedades?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  });
}

/** Organization enxuta (marca / entidade). */
export function buildOrganizationSchema(settings, baseUrl) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const mediaUrl = resolveSchemaMediaUrl(settings, base);
  const telephone = formatSchemaTelephone(
    settings?.contactPhone || settings?.contactWhatsapp || ''
  );
  const sameAs = normalizeSameAsUrls([
    settings?.socialFacebook,
    settings?.socialInstagram,
    settings?.socialLinkedin,
    settings?.socialTwitter,
  ]);

  return stripUndefinedDeep({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${base}/#organization`,
    name: settings?.companyName || 'Imobiliária',
    url: `${base}/`,
    logo: mediaUrl,
    telephone,
    email: settings?.contactEmail || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  });
}

/** RealEstateAgent / LocalBusiness com dados locais. */
export function buildRealEstateAgentSchema(settings, baseUrl) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const address = buildBusinessPostalAddress(settings);
  const mediaUrl = resolveSchemaMediaUrl(settings, base);
  const telephone = formatSchemaTelephone(
    settings?.contactPhone || settings?.contactWhatsapp || ''
  );
  const businessType =
    settings?.seoBusinessType && settings.seoBusinessType !== 'Organization'
      ? settings.seoBusinessType
      : 'RealEstateAgent';

  return stripUndefinedDeep({
    '@context': 'https://schema.org',
    '@type': businessType,
    '@id': `${base}/#realestateagent`,
    name: settings?.companyName || 'Imobiliária',
    url: `${base}/`,
    description: settings?.seoMetaDescription || undefined,
    telephone,
    email: settings?.contactEmail || undefined,
    priceRange: resolvePriceRange(settings),
    logo: mediaUrl,
    image: mediaUrl,
    address,
    areaServed: buildAreaServed(settings),
    openingHoursSpecification: buildOpeningHoursSpecification(settings),
    parentOrganization: { '@id': `${base}/#organization` },
  });
}

/** Pacote padrão de schemas de site (sem duplicar). */
export function buildSiteJsonLdSchemas(settings, baseUrl) {
  return [
    buildWebSiteSchema(settings, baseUrl),
    buildOrganizationSchema(settings, baseUrl),
    buildRealEstateAgentSchema(settings, baseUrl),
    buildSiteNavigationElementSchema(baseUrl),
  ].filter(Boolean);
}

/** Reforça candidatos a sitelinks (Google não garante exibição). */
export function buildSiteNavigationElementSchema(baseUrl) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const items = [
    { name: 'Início', url: `${base}/` },
    { name: 'Propriedades', url: `${base}/propriedades` },
    { name: 'Condomínios', url: `${base}/condominios` },
    { name: 'Contato', url: `${base}/contato` },
    { name: 'Sobre nós', url: `${base}/sobre` },
    { name: 'Blog', url: `${base}/blog` },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'SiteNavigationElement',
      position: index + 1,
      name: item.name,
      url: item.url,
    })),
  };
}

export function buildCollectionPageSchema({
  name,
  description,
  url,
  totalItems,
  items = [],
  baseUrl,
}) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const canonicalUrl = url?.startsWith('http')
    ? normalizeProductionUrl(url, base)
    : normalizeProductionUrl(url || '/propriedades', base);

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: canonicalUrl,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: totalItems ?? items.length,
      itemListElement: items.slice(0, 20).map((prop, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: buildPropertyUrl(prop, base),
        name: prop.seo?.metaTitle || prop.title || getPropertyTypeLabel(prop.type),
      })),
    },
  };
}

export function buildImageObjectSchema(imageUrl, property, baseUrl) {
  if (!imageUrl) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: imageUrl,
    url: imageUrl,
    name: property?.seo?.metaTitle || property?.title || 'Imóvel',
    description: property?.seo?.metaDescription || '',
    ...(property?.id
      ? { isPartOf: { '@type': 'WebPage', url: buildPropertyUrl(property, baseUrl) } }
      : {}),
  };
}

export function buildListingSeoMeta(filters, options = {}) {
  const { companyName, cityName, neighborhoodName, typeName, totalCount = 0 } = options;
  const brand = companyName || 'Imobiliária';

  if (cityName && typeName) {
    const label = getPropertyTypeLabel(typeName) || typeName;
    const local = neighborhoodName ? `${neighborhoodName}, ${cityName}` : cityName;
    return {
      title: truncateSeoTitle(`${label} em ${local} | ${brand}`),
      description: truncateSeoDescription(
        `Confira ${totalCount || ''} ${label.toLowerCase()} em ${local}. Encontre o imóvel ideal com ${brand}.`
      ),
      heading: `${label} em ${local}`,
    };
  }

  if (neighborhoodName && cityName) {
    return {
      title: truncateSeoTitle(`Imóveis no ${neighborhoodName}, ${cityName} | ${brand}`),
      description: truncateSeoDescription(
        `Encontre ${totalCount || ''} imóveis no bairro ${neighborhoodName}, ${cityName}. Casas, apartamentos e comerciais para compra e aluguel.`
      ),
      heading: `Imóveis no ${neighborhoodName}, ${cityName}`,
    };
  }

  if (cityName) {
    return {
      title: truncateSeoTitle(`Imóveis em ${cityName} | ${brand}`),
      description: truncateSeoDescription(
        `Explore imóveis em ${cityName}. ${totalCount ? `${totalCount} opções` : 'Casas, apartamentos e terrenos'} para compra e aluguel.`
      ),
      heading: `Imóveis em ${cityName}`,
    };
  }

  if (typeName) {
    const label = getPropertyTypeLabel(typeName) || typeName;
    return {
      title: truncateSeoTitle(`${label} para compra e aluguel | ${brand}`),
      description: truncateSeoDescription(
        `Confira ${totalCount || ''} ${label.toLowerCase()} disponíveis. Encontre o imóvel ideal com ${brand}.`
      ),
      heading: `${label} para compra e aluguel`,
    };
  }

  if (filters?.search) {
    return {
      title: truncateSeoTitle(`Busca: ${filters.search} | Imóveis | ${brand}`),
      description: truncateSeoDescription(
        `Resultados de imóveis para "${filters.search}". Compre, venda ou alugue com ${brand}.`
      ),
      heading: `Resultados para "${filters.search}"`,
    };
  }

  return {
    title: truncateSeoTitle(`${brand} - Imóveis | Compra, Venda e Aluguel`),
    description: truncateSeoDescription(
      'Explore nossa seleção de imóveis. Encontre casas, apartamentos, comerciais e terrenos para compra e aluguel.'
    ),
    heading: 'Encontre o imóvel dos seus sonhos',
  };
}
